/**
 * The interactive city.
 *
 * The generated master is neutral and unlit for ownership, so every piece of state --
 * control, Heat, the Beat, selection, hover -- is drawn live in SVG on top of it. That
 * means one 2048px image serves every possible board state, and territory flips are a
 * data change rather than a re-render of the art.
 *
 * Ownership is encoded three ways at once (colour + pattern + marker) so it survives
 * grayscale and red/green colour blindness.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { A } from '../art'
import { Icon } from '../ui/Icon'
import { Row } from '../ui/Kit'
import { sz, useViewport } from '../ui/viewport'
import {
  districts,
  controlLabel,
  controlPattern,
  controlTint,
  useGame,
  type District,
} from '../game/state'

const pts = (d: District) => d.poly.map(([x, y]) => `${x},${y}`).join(' ')

function Defs() {
  return (
    <defs>
      {/* Ownership patterns. Deliberately monochrome: colour is applied by the polygon. */}
      <pattern id="pat-hatch" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
        <line x1="0" y1="0" x2="0" y2="6" stroke="#fff" strokeWidth="2.2" opacity=".5" />
      </pattern>
      <pattern id="pat-dots" width="5" height="5" patternUnits="userSpaceOnUse">
        <circle cx="2.5" cy="2.5" r="1.15" fill="#fff" opacity=".55" />
      </pattern>
      <pattern id="pat-cross" width="7" height="7" patternUnits="userSpaceOnUse">
        <path d="M0 0H7M0 0V7" stroke="#fff" strokeWidth="1.5" opacity=".45" />
      </pattern>
      <pattern id="pat-hazard" width="9" height="9" patternUnits="userSpaceOnUse" patternTransform="rotate(-45)">
        <rect width="4.5" height="9" fill="#fff" opacity=".42" />
      </pattern>
      <pattern id="pat-stipple" width="9" height="9" patternUnits="userSpaceOnUse">
        <circle cx="2" cy="2" r=".9" fill="#fff" opacity=".3" />
        <circle cx="6.5" cy="6" r=".9" fill="#fff" opacity=".3" />
      </pattern>

      <filter id="glow" x="-40%" y="-40%" width="180%" height="180%">
        <feGaussianBlur stdDeviation="1.4" result="b" />
        <feMerge>
          <feMergeNode in="b" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>

      <filter id="hot" x="-60%" y="-60%" width="220%" height="220%">
        <feGaussianBlur stdDeviation="3" />
      </filter>
    </defs>
  )
}

/** Distinct marker shape per control state -- the third redundant ownership channel. */
function Marker({ c, x, y }: { c: District['control']; x: number; y: number }) {
  const tint = controlTint[c]
  const common = { fill: tint, stroke: '#05080a', strokeWidth: 0.35, filter: 'url(#glow)' }
  if (c === 'you') return <circle cx={x} cy={y} r={1.5} {...common} />
  if (c === 'allied') return <rect x={x - 1.3} y={y - 1.3} width={2.6} height={2.6} {...common} />
  if (c === 'contested')
    return <polygon points={`${x},${y - 1.7} ${x + 1.7},${y} ${x},${y + 1.7} ${x - 1.7},${y}`} {...common} />
  if (c === 'enemy')
    return <polygon points={`${x},${y - 1.8} ${x + 1.6},${y + 1.2} ${x - 1.6},${y + 1.2}`} {...common} />
  return <circle cx={x} cy={y} r={1.1} fill="none" stroke={tint} strokeWidth={0.55} />
}

export function CityMap({ onOpen }: { onOpen?: (d: District) => void }) {
  const { selected, select, beat, player } = useGame()
  // The label for a district you hold names YOUR family, not a constant. It read "Marchetti
  // Crew" for every account in the game, including every account with no family at all.
  const family = player.family
  const [hover, setHover] = useState<string | null>(null)
  const [zoom, setZoom] = useState(1)
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const drag = useRef<{ x: number; y: number; px: number; py: number } | null>(null)

  /*
   * THE MAP KNOWS HOW WIDE IT IS, since Phase 18. Twelve labels at a fixed 12.5px collided from
   * about 1,150px down ("North Quay" ran into "Slaughterhouse Flats" at 1366), and the legend and
   * the zoom controls sat over Harbor Ward and Dockside. Labels now scale with the box and, under
   * 560px, only the Beat's and the selected district's are drawn: the rest are the readout's job.
   * The legend folds into a button under 900px. Nothing about the geometry changes.
   */
  const box = useRef<HTMLDivElement>(null)
  const [w, setW] = useState(1200)
  useEffect(() => {
    const el = box.current
    if (!el) return
    const measure = () => setW(el.clientWidth)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  const { touch } = useViewport()
  const labelPx = Math.max(touch ? 11 : 9, Math.min(12.5, w / 92))
  // On touch the floor is 11px, so the labels need more room before all twelve fit.
  const fewLabels = w < (touch ? 760 : 560)
  const [legendOpen, setLegendOpen] = useState(false)
  const legendFolded = w < 900

  const onDown = useCallback(
    (e: React.PointerEvent) => {
      if (zoom <= 1) return
      drag.current = { x: e.clientX, y: e.clientY, px: pan.x, py: pan.y }
      ;(e.target as Element).setPointerCapture?.(e.pointerId)
    },
    [zoom, pan],
  )
  const onMove = useCallback((e: React.PointerEvent) => {
    const d = drag.current
    if (!d) return
    setPan({ x: d.px + (e.clientX - d.x), y: d.py + (e.clientY - d.y) })
  }, [])
  const onUp = useCallback(() => {
    drag.current = null
  }, [])

  const reset = () => {
    setZoom(1)
    setPan({ x: 0, y: 0 })
  }

  const active = hover ?? selected
  const activeD = districts().find((d) => d.id === active)

  return (
    <div
      ref={box}
      style={{
        position: 'relative',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        background: '#04070a',
        border: '1px solid var(--line)',
        cursor: zoom > 1 ? (drag.current ? 'grabbing' : 'grab') : 'default',
      }}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerLeave={() => {
        onUp()
        setHover(null)
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
          transformOrigin: 'center',
          transition: drag.current ? 'none' : 'transform 320ms cubic-bezier(.4,0,.2,1)',
        }}
      >
        <img
          src={A('city.master')}
          alt="Ashport, twelve districts across the harbour"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
        />

        <svg
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
        >
          <Defs />

          {districts().map((d) => {
            const on = active === d.id
            const sel = selected === d.id
            const isBeat = beat.district === d.id
            const tint = controlTint[d.control]
            return (
              <g
                key={d.id}
                onMouseEnter={() => setHover(d.id)}
                onMouseLeave={() => setHover((h) => (h === d.id ? null : h))}
                onClick={() => select(d.id)}
                onDoubleClick={() => onOpen?.(d)}
                style={{ cursor: 'pointer' }}
                tabIndex={0}
                role="button"
                aria-label={`${d.name}, ${controlLabel(d.control, family)}`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    select(d.id)
                  }
                }}
              >
                {/* Fill stays faint: the art is the point, the overlay only annotates it. */}
                <polygon points={pts(d)} fill={tint} opacity={on ? 0.19 : sel ? 0.16 : 0.075} />
                {/* redundant pattern channel */}
                <polygon
                  points={pts(d)}
                  fill={`url(#${controlPattern[d.control]})`}
                  opacity={on ? 0.22 : 0.1}
                  style={{ color: tint }}
                />
                {/* edge */}
                <polygon
                  points={pts(d)}
                  fill="none"
                  stroke={tint}
                  strokeWidth={on || sel ? 0.62 : 0.32}
                  opacity={on || sel ? 1 : 0.62}
                  filter={on || sel ? 'url(#glow)' : undefined}
                  strokeDasharray={d.control === 'contested' ? '2.4 1.4' : undefined}
                >
                  {d.control === 'contested' && (
                    <animate attributeName="stroke-dashoffset" from="0" to="7.6" dur="1.1s" repeatCount="indefinite" />
                  )}
                </polygon>

                {/* the Beat: a pulsing red bloom the eye is pulled to first */}
                {isBeat && (
                  <>
                    <polygon points={pts(d)} fill="#f05a32" opacity={0.2} filter="url(#hot)">
                      <animate attributeName="opacity" values="0.1;0.26;0.1" dur="2.6s" repeatCount="indefinite" />
                    </polygon>
                    <polygon points={pts(d)} fill="none" stroke="#ff7a4a" strokeWidth={0.9} filter="url(#glow)">
                      <animate attributeName="opacity" values="0.55;1;0.55" dur="2.6s" repeatCount="indefinite" />
                    </polygon>
                  </>
                )}

                <Marker c={d.control} x={d.at[0]} y={d.at[1] + 4.2} />
              </g>
            )
          })}
        </svg>

        {/* District labels live in HTML so they stay crisp and selectable at any zoom. */}
        {districts().map((d) => {
          const on = active === d.id
          const isBeat = beat.district === d.id
          if (fewLabels && !on && !isBeat) return null
          return (
            <div
              key={d.id}
              style={{
                position: 'absolute',
                left: `${d.at[0]}%`,
                top: `${d.at[1]}%`,
                transform: `translate(-50%,-50%) scale(${1 / zoom})`,
                pointerEvents: 'none',
                textAlign: 'center',
                transition: 'opacity 180ms ease',
                opacity: on || isBeat ? 1 : 0.86,
              }}
            >
              <div
                className="t-head"
                style={{
                  fontSize: labelPx,
                  letterSpacing: '.15em',
                  color: on || isBeat ? '#fff' : 'rgba(238,242,244,.9)',
                  textShadow: '0 1px 3px #000, 0 0 14px rgba(0,0,0,.95), 0 0 26px rgba(0,0,0,.8)',
                  whiteSpace: 'nowrap',
                }}
              >
                {d.short}
              </div>
              {isBeat && (
                <div
                  className="t-label pulse"
                  style={{ fontSize: sz(8.5), color: 'var(--heat)', textShadow: '0 1px 3px #000', marginTop: 1 }}
                >
                  The Beat
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* --- hover readout ------------------------------------------------ */}
      {activeD && (
        <div
          className="panel-flat"
          style={{
            position: 'absolute',
            // A 246px card at the top-left covers a third of a phone's map. There it is a strip
            // along the bottom edge instead, under the district it describes.
            ...(w < 560
              ? { left: 8, right: 8, bottom: 8, padding: '7px 10px' }
              : { left: 12, top: 12, width: 246, padding: '9px 11px' }),
            pointerEvents: 'none',
            background: 'rgba(6,9,11,.93)',
            borderColor: controlTint[activeD.control],
          }}
        >
          <Row gap={7}>
            <span className="t-head" style={{ fontSize: 13, color: 'var(--ink-hi)', letterSpacing: '.1em' }}>
              {activeD.name}
            </span>
          </Row>
          <div
            className="t-label"
            style={{ color: controlTint[activeD.control], marginTop: 2, fontSize: sz(9) }}
          >
            {controlLabel(activeD.control, family)}
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink-mid)', marginTop: 5, lineHeight: 1.4 }}>
            {activeD.blurb}
          </div>
          <Row gap={12} style={{ marginTop: 7, borderTop: '1px solid var(--line-soft)', paddingTop: 6 }}>
            <div>
              <div className="t-label" style={{ fontSize: sz(8.5) }}>Blocks</div>
              <div className="t-num" style={{ fontSize: 13 }}>{activeD.blocks}</div>
            </div>
            <div>
              <div className="t-label" style={{ fontSize: sz(8.5) }}>Daily</div>
              <div className="t-num" style={{ fontSize: 13, color: 'var(--clean)' }}>
                ${(activeD.income / 1000).toFixed(0)}K
              </div>
            </div>
            <div>
              <div className="t-label" style={{ fontSize: sz(8.5) }}>Heat</div>
              <div className="t-num" style={{ fontSize: 13, color: 'var(--heat)' }}>{activeD.heat}%</div>
            </div>
          </Row>
        </div>
      )}

      {/* --- zoom controls ------------------------------------------------ */}
      <div style={{ position: 'absolute', right: 12, top: '46%', display: 'flex', flexDirection: 'column', gap: 5 }}>
        {[
          { i: 'center' as const, f: reset, t: 'Reset view' },
          { i: 'plus' as const, f: () => setZoom((z) => Math.min(3.2, z * 1.35)), t: 'Zoom in' },
          { i: 'minus' as const, f: () => setZoom((z) => Math.max(1, z / 1.35)), t: 'Zoom out' },
        ].map(({ i, f, t }) => (
          <button
            key={i}
            onClick={f}
            title={t}
            aria-label={t}
            style={{
              width: touch ? 44 : 30,
              height: touch ? 44 : 30,
              display: 'grid',
              placeItems: 'center',
              background: 'rgba(11,15,18,.9)',
              border: '1px solid var(--line)',
              cursor: 'pointer',
              transition: 'border-color 140ms ease',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'var(--line-brass-hot)')}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--line)')}
          >
            <Icon n={i} s={15} c="var(--ink-mid)" />
          </button>
        ))}
      </div>

      {/* --- legend -------------------------------------------------------- */}
      {legendFolded && !legendOpen ? (
        <button
          onClick={() => setLegendOpen(true)}
          aria-label="Show the territory key"
          className="t-label"
          style={{
            position: 'absolute',
            right: 12,
            top: 12,
            minHeight: touch ? 44 : 28,
            minWidth: touch ? 44 : undefined,
            padding: '0 10px',
            background: 'rgba(6,9,11,.9)',
            border: '1px solid var(--line)',
            color: 'var(--ink-mid)',
            cursor: 'pointer',
            fontSize: sz(8.5),
          }}
        >
          Key
        </button>
      ) : (
        <div
          className="panel-flat"
          style={{
            position: 'absolute',
            right: 12,
            ...(legendFolded ? { top: 12 } : { bottom: 12 }),
            padding: '8px 11px',
            background: 'rgba(6,9,11,.9)',
          }}
        >
          <Row gap={8} style={{ marginBottom: 6 }}>
            <div className="t-label" style={{ fontSize: sz(8.5), flex: 1 }}>Territory Control</div>
            {legendFolded && (
              <button
                onClick={() => setLegendOpen(false)}
                aria-label="Hide the territory key"
                className="icon-btn"
                style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', lineHeight: 0 }}
              >
                <Icon n="close" s={11} c="var(--ink-dim)" />
              </button>
            )}
          </Row>
          {(['you', 'allied', 'contested', 'enemy', 'neutral'] as const).map((c) => (
            <Row key={c} gap={7} style={{ marginTop: 3 }}>
              <svg width="12" height="12" viewBox="0 0 12 12" style={{ flex: 'none' }}>
                <rect width="12" height="12" fill={controlTint[c]} opacity=".28" />
                <rect width="12" height="12" fill={`url(#${controlPattern[c]})`} opacity=".4" />
                <rect width="12" height="12" fill="none" stroke={controlTint[c]} strokeWidth="1" />
              </svg>
              <span style={{ fontSize: sz(10.5), color: 'var(--ink-mid)' }}>{controlLabel(c, family)}</span>
            </Row>
          ))}
          {/* patterns must exist in this SVG scope too */}
          <svg width="0" height="0" style={{ position: 'absolute' }}>
            <Defs />
          </svg>
        </div>
      )}
    </div>
  )
}
